/* =========================================================================
   BIGOLI — shared filter engine.

   ONE ENGINE, TWO PLACES. The shape picker and the menu use this same file
   and the same markup snippet (snippets/bgl-filter.liquid). If you are about
   to copy this logic into a section, stop and add a data attribute instead.

   It knows nothing about pasta. It filters any list of things that carry
   space-separated tokens, using an expression written on the control.

   MARKUP CONTRACT
   ---------------
   [data-bgl-filter]              the scope. Must contain the controls, the
                                  items, the count and the live region.
     data-bgl-filter-noun="..."   plural noun used in the count sentence
     data-bgl-filter-total="16"   optional; defaults to the item count
     data-bgl-filter-select="radio"
                                  the items are radio inputs and exactly one
                                  must stay checked and reachable

   [data-bgl-filter-control]      a radio input, one per filter option
     value="vegan"                the mode name
     data-bgl-match="vegan"       the expression, see below

   [data-bgl-filter-item]         the thing being filtered. Carries
     data-diet="vegan nuts"       its tokens.

   [data-bgl-filter-cell]         the ancestor that gets hidden. Defaults to
                                  the item itself if there is no such
                                  ancestor.

   [data-bgl-filter-group]        a heading + list that should disappear
                                  when everything inside it is filtered out.
                                  A group containing NO cells at all is
                                  informational, not filterable, and is
                                  never hidden — see the note below.

   [data-bgl-filter-count]        text node for "Vegan: 4 of 16 sauces."
   [data-bgl-filter-live]         aria-live="polite" region.

   EXPRESSIONS
   -----------
     *                every item matches
     vegan            item must carry the token
     vegan|vegetarian item must carry at least one of them
     !nuts            item must NOT carry the token

   WHAT IT EMITS
   -------------
   A bubbling `bgl:filter` CustomEvent on the scope element, with
   detail = { mode, label, visible, total, moved }. The picker listens for
   this to repaint its ticket. Nothing else in here knows the ticket exists.

   WITHOUT JAVASCRIPT
   ------------------
   Nothing here runs, nothing is hidden, and every item is visible — the
   markup ships in the "everything" state. The snippet additionally emits a
   <noscript> stylesheet that reproduces the filtering in pure CSS, so the
   controls still work; only the live count is a JS enhancement.
   ========================================================================= */
(function () {
  "use strict";

  function slice(list) { return Array.prototype.slice.call(list); }

  function tokensOf(el) {
    var raw = (el.getAttribute('data-diet') || '').trim();
    return raw ? raw.split(/\s+/) : [];
  }

  function matches(item, expr) {
    if (!expr || expr === '*') { return true; }
    var toks = tokensOf(item);
    var parts = expr.split('|');
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].replace(/^\s+|\s+$/g, '');
      if (!p) { continue; }
      if (p.charAt(0) === '!') {
        if (toks.indexOf(p.slice(1)) === -1) { return true; }
      } else if (toks.indexOf(p) !== -1) {
        return true;
      }
    }
    return false;
  }

  function checkedOf(list) {
    for (var i = 0; i < list.length; i++) { if (list[i].checked) { return list[i]; } }
    return null;
  }

  function labelFor(scope, input) {
    var lab = scope.querySelector('label[for="' + input.id + '"]');
    return lab ? lab.textContent.replace(/\s+/g, ' ').replace(/^ | $/g, '') : input.value;
  }

  function init(scope) {
    if (scope.getAttribute('data-bgl-filter-ready') === '1') { return; }
    scope.setAttribute('data-bgl-filter-ready', '1');

    var controls = slice(scope.querySelectorAll('[data-bgl-filter-control]'));
    var items    = slice(scope.querySelectorAll('[data-bgl-filter-item]'));
    var countEl  = scope.querySelector('[data-bgl-filter-count]');
    var live     = scope.querySelector('[data-bgl-filter-live]');

    /* A group with no cells inside it is a note, not a list — the "Pick a
       pasta shape" panel on the menu is one. Hiding it because it contains
       nothing filterable would delete real content the moment the page
       loaded, so those groups are excluded here rather than special-cased
       in the sections. */
    var groups = slice(scope.querySelectorAll('[data-bgl-filter-group]')).filter(function (g) {
      return g.querySelectorAll('[data-bgl-filter-cell]').length > 0;
    });

    if (!controls.length || !items.length) { return; }

    var noun    = scope.getAttribute('data-bgl-filter-noun') || 'items';
    var total   = parseInt(scope.getAttribute('data-bgl-filter-total'), 10) || items.length;
    var isRadio = scope.getAttribute('data-bgl-filter-select') === 'radio';

    var cells = items.map(function (it) {
      return (it.closest && it.closest('[data-bgl-filter-cell]')) || it;
    });

    function announce(sentence) {
      if (!live) { return; }
      live.textContent = '';
      window.setTimeout(function () { live.textContent = sentence; }, 60);
    }

    function apply(shouldAnnounce) {
      var ctrl    = checkedOf(controls) || controls[0];
      var mode    = ctrl.value;
      var expr    = ctrl.getAttribute('data-bgl-match') || '*';
      var label   = labelFor(scope, ctrl);
      var focused = document.activeElement;
      var visible = [];
      var i;

      for (i = 0; i < items.length; i++) {
        var ok = matches(items[i], expr);
        /* `hidden` takes the item out of the tab order AND the accessibility
           tree, so a filtered-out dish can never be reached or announced. */
        cells[i].hidden = !ok;
        if (ok) { visible.push(items[i]); }
      }

      for (i = 0; i < groups.length; i++) {
        groups[i].hidden = (groups[i].querySelectorAll('[data-bgl-filter-cell]:not([hidden])').length === 0);
      }

      /* Radio lists must never be left with the selection inside something
         we just hid — the picker's ticket would describe an invisible dish. */
      var moved = false;
      if (isRadio) {
        var current = checkedOf(items);
        if (!current || cells[items.indexOf(current)].hidden) {
          if (current) { current.checked = false; }
          if (visible.length) { visible[0].checked = true; moved = true; }
        }
        var stranded = focused && focused.closest &&
                       focused.closest('[data-bgl-filter-cell]') &&
                       focused.closest('[data-bgl-filter-cell]').hidden;
        if (stranded) {
          var target = checkedOf(items);
          if (target && target.focus) { target.focus(); }
        }
      }

      if (countEl) {
        countEl.textContent = (expr === '*')
          ? 'Showing all ' + total + ' ' + noun + '.'
          : label + ': ' + visible.length + ' of ' + total + ' ' + noun + '.';
      }

      scope.dispatchEvent(new CustomEvent('bgl:filter', {
        bubbles: true,
        detail: { mode: mode, label: label, visible: visible.length, total: total, moved: moved }
      }));

      if (shouldAnnounce) {
        var chosen = isRadio && moved ? checkedOf(items) : null;
        var tail = chosen ? ', so your choice changed to ' + chosen.getAttribute('data-name') : '';
        announce(expr === '*'
          ? 'Filter cleared, all ' + total + ' ' + noun + ' showing' + tail + '.'
          : label + ' filter on, ' + visible.length + ' of ' + total + ' ' + noun + ' showing' + tail + '.');
      }
    }

    scope.addEventListener('change', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-bgl-filter-control')) { apply(true); }
    });

    apply(false);
  }

  function boot() {
    slice(document.querySelectorAll('[data-bgl-filter]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: sections are re-rendered without a page load. */
  document.addEventListener('shopify:section:load', boot);
})();